import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Check, Circle, Copy, Link2, Mail } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { generateIntakeLink, sendIntakeInvite } from '../../services/api';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import { useAsync } from '../aai/useAsync';
import type { PartnerClientRow, PartnerOverview } from '../../types/aai';
import { Action, Banner, EmptyState, Field, Input, Loading, Modal, PageBody, Panel } from '../../components/aa';
import { ClientHeader } from './ClientHeader';
import { OrbitRoomPanel } from '../orbit-room/OrbitRoomPanel';
// Documents hidden for now.
// import { DocumentLibrary } from './DocumentLibrary';


/*
 * The client's page is the Orbit Room, in the 14 Sep artifact's shape:
 * Overview, Dossiers and Opportunities as the page's own tabs, with the
 * client's name in the eyebrow and Adjust framework at the right. The team and
 * the departments live inside the People dossier; the invite keeps its place
 * above until it has gone out.
 */

const INVITE_DAYS = 14;

/*
 * The invite that follows Add a client, the same two ways the handover screen
 * offers it: copy the Orbit intake link to pass on yourself, or email it. Both
 * carry everything the partner entered. Once the client has opened the link
 * there is nothing left to hand over, so these exist only while they are Draft
 * or Invited.
 */
const CopyInviteLink: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    setBusy(true);
    try {
      const url = link || (await generateIntakeLink(clientId, INVITE_DAYS)).url;
      setLink(url);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success(`Invite link copied. It expires in ${INVITE_DAYS} days.`);
      } catch {
        toast.info(`The link is ready: ${url}`);
      }
    } catch (err) {
      toast.error((err as Error).message || 'Could not prepare the invite link.');
    } finally {
      setBusy(false);
    }
  };

  const icon = () => {
    if (copied) return <Check size={14} strokeWidth={2.5} />;
    if (link) return <Copy size={14} />;
    return <Link2 size={14} />;
  };

  const label = () => {
    if (copied) return 'Link copied';
    if (link) return 'Copy client link';
    return 'Prepare client link';
  };

  return (
    <Action
      variant="quiet"
      icon={icon()}
      onClick={copy}
      disabled={busy}
      reason="Preparing the link"
      className="whitespace-nowrap"
    >
      {label()}
    </Action>
  );
};

/* The invite emails a real client, so it names the recipient first and goes
   to exactly the address shown, which the partner can correct. */
const InviteOwner: React.FC<{ clientId: string; clientName: string; email: string | null; resend: boolean }> = ({
  clientId, clientName, email, resend,
}) => {
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [recipient, setRecipient] = useState(email || '');

  const invite = async () => {
    setBusy(true);
    try {
      const res = await sendIntakeInvite(clientId, INVITE_DAYS, recipient.trim());
      toast.success(`Invite sent to ${res.email}. The link expires in ${INVITE_DAYS} days.`);
      setAsking(false);
    } catch (err) {
      toast.error((err as Error).message || 'Could not send the invite.');
    } finally {
      setBusy(false);
    }
  };

  let label = 'Send client invite';
  if (resend) label = 'Resend client invite';
  let reason = '';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim())) reason = 'Enter the owner\'s email address';
  if (busy) reason = 'Sending the invite';

  return (
    <>
      <Action
        variant="quiet"
        icon={<Mail size={14} />}
        onClick={() => { setRecipient(email || ''); setAsking(true); }}
        className="whitespace-nowrap"
      >
        {label}
      </Action>
      {asking && (
        <Modal
          size="dialog"
          context="Client invite"
          onClose={() => setAsking(false)}
          footer={(
            <div className="flex justify-end gap-2">
              <Action variant="ghost" onClick={() => setAsking(false)}>Cancel</Action>
              <Action icon={<Mail size={14} />} onClick={invite} disabled={!!reason} reason={reason}>Send invite</Action>
            </div>
          )}
        >
          <h2 className="m-0 text-[22px] font-bold leading-[29px] tracking-[-0.015em] text-ink">Send the invite to {clientName}&rsquo;s owner?</h2>
          <p className="m-0 mt-2 text-[13.5px] leading-[22px] text-ink-muted">
            They get an email with a link to set up their company with Orbit. The link expires in {INVITE_DAYS} days.
          </p>
          <Field label="Send to" htmlFor="invite-recipient" className="mt-5" hint="Check the address. The email goes out as soon as you press Send invite.">
            <Input id="invite-recipient" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} autoComplete="off" />
          </Field>
        </Modal>
      )}
    </>
  );
};

/* What the partner can get ready before the owner gets a link. Schedule is
   parked, so it is not on the list. Nothing here blocks sending. */
const SetupChecklist: React.FC<{ row: PartnerClientRow }> = ({ row }) => {
  const items = [
    { key: 'framework', done: true, title: 'Framework', detail: 'Decides what this client is asked.', to: '/partner/framework', action: 'Review' },
    {
      key: 'intake',
      done: row.onboarding_total > 0 && row.onboarding_captured >= row.onboarding_total,
      title: 'Intake',
      detail: `${row.onboarding_captured} of ${row.onboarding_total} company questions answered. Answer what you know in the intake below.`,
      to: '',
      action: '',
    },
    // Documents hidden for now.
    // { key: 'documents', done: row.documents > 0, title: 'Documents (optional)', detail: `${row.documents} added for Orbit to read.`, to: '#documents', action: 'Add' },
  ];
  return (
    <ul className="m-0 mt-2 list-none space-y-1.5 p-0">
      {items.map((item) => {
        let mark = <Circle size={14} className="text-ink-dim" />;
        if (item.done) mark = <Check size={14} strokeWidth={2.5} className="text-[#5da14a]" />;
        return (
          <li key={item.key} className="flex items-start gap-2">
            <span className="mt-[3px] shrink-0">{mark}</span>
            <span>
              <b className="text-ink">{item.title}</b>: {item.detail}
              {item.to && <> <Link to={item.to} className="font-semibold text-ink underline">{item.action}</Link></>}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const ClientOverviewPage: React.FC = () => {
  const { active, loading: clientsLoading } = useActiveClient();
  const { hash } = useLocation();
  const overview = useAsync<PartnerOverview>(() => aaiApi.getPartnerOverview(), 'Could not load client information.', [active?.id]);

  // Go to's "Client documents" lands here with #documents. The section sits
  // under the Orbit Room, so it is scrolled to once the page has a client.
  useEffect(() => {
    if (hash !== '#documents' || !active) return;
    const t = window.setTimeout(() => document.getElementById('documents')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    return () => window.clearTimeout(t);
  }, [hash, active]);

  if (clientsLoading) return <PageBody><Loading label="Loading your clients" /></PageBody>;

  if (!active) {
    return (
      <PageBody>
        <Panel>
          <EmptyState
            title="No client selected"
            reason="Open a client from the Clients list to work inside it."
            action={<Action to="/partner/clients">Open clients</Action>}
          />
        </Panel>
      </PageBody>
    );
  }

  const row = overview.data?.clients.find((c) => c.id === active.id) || null;
  // The invite has its own life: a partner may answer cards, or close the deck,
  // before any link exists, so "onboarding" says nothing about whether one went out.
  const inviteNotSent = row !== null && !row.invite_issued_at && row.status !== 'active';
  const inviteOut = row !== null && row.status === 'invited';

  return (
    <PageBody>
      <ClientHeader
        client={active}
        tabs={false}
        shared={false}
        actions={inviteOut && (
          <>
            <CopyInviteLink clientId={active.id} />
            <InviteOwner clientId={active.id} clientName={active.name} email={row?.contact_email || null} resend />
          </>
        )}
      />
      {inviteNotSent && (
        <Banner
          className="mb-5"
          tone="quiet"
          size="md"
          kicker="Invitation not sent"
          title={`${active.name}'s owner has no link yet`}
          desc={(
            <>
              Nothing reaches the owner until you share a link or send the invite. Anything you answered is kept and shown to them as facts to confirm.
              {row && <SetupChecklist row={row} />}
            </>
          )}
          action={(
            <div className="flex flex-wrap items-center gap-2">
              <CopyInviteLink clientId={active.id} />
              <InviteOwner clientId={active.id} clientName={active.name} email={row?.contact_email || null} resend={false} />
            </div>
          )}
        />
      )}
      <OrbitRoomPanel viewer="partner" clientId={active.id} clientName={active.name} layout="page" />
      {/* Documents hidden for now. The client's own documents, the same
          library its Owner keeps.
      <div id="documents" className="mt-5 scroll-mt-4">
        <DocumentLibrary
          key={active.id}
          head={{
            kicker: 'Client documents',
            title: `Documents for ${active.name}`,
            desc: 'Add the files this client works from. What you add for the whole company or a department, its owner sees too; a personal one stays between you and the owner.',
          }}
        />
      </div>
      */}
    </PageBody>
  );
};

export default ClientOverviewPage;
